import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export function useClients() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getClients = useCallback(async ({ search, userId, businessId } = {}) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('clients')
        .select('*')
        .order('name', { ascending: true });

      if (userId) query = query.eq('user_id', userId);
      if (businessId) query = query.eq('business_id', businessId);
      if (search) {
        query = query.or(
          `name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getClientById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)  // defense-in-depth: RLS + explicit user check
        .single();
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getClientWithQuotes = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const clientRes = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)  // defense-in-depth: RLS + explicit user check
        .single();
      if (clientRes.error) throw clientRes.error;
      const client = clientRes.data;

      // Match quotes by email (most reliable) with fallback to name, scoped to this user
      let quotesQuery = supabase
        .from('quotes')
        .select('id, job_description, total, status, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (client.email) {
        quotesQuery = quotesQuery.eq('client_email', client.email);
      } else {
        quotesQuery = quotesQuery.eq('client_name', client.name);
      }

      const quotesRes = await quotesQuery;
      return {
        client,
        quotes: quotesRes.data || [],
      };
    } catch (err) {
      setError(err.message);
      return { client: null, quotes: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  const createClient = useCallback(async (clientData) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('clients')
        .insert({
          ...clientData,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateClient = useCallback(async (id, updates) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteClient = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('clients').delete().eq('id', id).eq('user_id', user.id);
      if (error) throw error;
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getClientsWithStats = useCallback(async (userId, businessId) => {
    setLoading(true);
    setError(null);
    try {
      let clientsQuery = supabase.from('clients').select('*').eq('user_id', userId).order('name');
      let quotesQuery = supabase.from('quotes').select('client_name, client_email, total, status').eq('user_id', userId);
      let invoicesQuery = supabase.from('invoices').select('client_name, client_email, total, status').eq('user_id', userId);
      if (businessId) {
        clientsQuery = clientsQuery.eq('business_id', businessId);
        quotesQuery = quotesQuery.eq('business_id', businessId);
        invoicesQuery = invoicesQuery.eq('business_id', businessId);
      }
      const [clientsRes, quotesRes, invoicesRes, jobsRes] = await Promise.all([
        clientsQuery,
        quotesQuery,
        invoicesQuery,
        supabase.from('jobs').select('client_id').eq('user_id', userId),
      ]);

      if (clientsRes.error) throw clientsRes.error;

      const clients = clientsRes.data || [];
      const quotes = quotesRes.data || [];
      const invoices = invoicesRes.data || [];
      const jobs = jobsRes.data || [];

      return clients.map((client) => {
        // Match by email first, fall back to name
        const match = (row) =>
          (client.email && row.client_email && row.client_email.toLowerCase() === client.email.toLowerCase()) ||
          (row.client_name && row.client_name.toLowerCase() === client.name.toLowerCase());

        const clientQuotes = quotes.filter(match);
        const clientInvoices = invoices.filter(match);

        const collected = clientInvoices
          .filter((i) => i.status === 'paid')
          .reduce((s, i) => s + (i.total || 0), 0);

        const invoiced = clientInvoices
          .reduce((s, i) => s + (i.total || 0), 0);

        const quoted = clientQuotes.reduce((s, q) => s + (q.total || 0), 0);

        // Best single "value" to show: collected > invoiced > quoted
        const totalValue = collected || invoiced || quoted;

        const jobCount = jobs.filter((j) => j.client_id === client.id).length;

        return {
          ...client,
          quoteCount: clientQuotes.length,
          invoiceCount: clientInvoices.length,
          jobCount,
          collected,
          invoiced,
          quoted,
          totalValue,
        };
      });
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    getClients,
    getClientById,
    getClientWithQuotes,
    createClient,
    updateClient,
    deleteClient,
    getClientsWithStats,
  };
}
